<script setup lang="ts">
import { getImageUrl } from '@/utils'
import { useFeatureItemClasses } from '@/composables/useFeatureItemClasses'
import { CustomerListItemsType, CustomerType, CustomerColorClassesType } from '@/types/customers-types'

import { useStore } from 'vuex'

const store = useStore()


const props = defineProps({
  type: {
    type: String as PropType<CustomerType>,
    default: 'primary',
  },
  title: {
    type: String as PropType<string>,
    default: '',
  },
  description: {
    type: String as PropType<string>,
    default: '',
  },
  imageName: {
    type: String as PropType<string>,
    default: '',
  },
  items: {
    type: Array as PropType<CustomerListItemsType>,
    default: () => [],
  },
  reverse: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  colorClasses: {
    type: String as PropType<CustomerColorClassesType>,
    default: 'bg-sand-100',
  },
  avatar: {
    type: String,
  },
  avatarText: {
    type: String,
  },
  avatarJob: {
    type: String,
  },
  id: {
    type: Number,
  },
})

const { colorClasses, reverse } = toRefs(props)

const isPrimary = computed<boolean>(() => props.type === 'primary')
const isSecondary = computed<boolean>(() => props.type === 'secondary')

const { itemClasses, headerClasses, titleClasses, descriptionClasses, badgeClasses } = useFeatureItemClasses({
  colorClasses,
  reverse,
  isPrimary,
  isSecondary,
})

const avatars = computed<string>(() => getImageUrl(props.avatar as string))
const imageSrc = computed<string>(() => getImageUrl(props.imageName))

const isPhoneOrTablet = inject('isPhoneOrTablet')
const isDesktop = inject('isDesktop')
</script>

<template>
  <youtube-popup v-if="store.state.openYoutubePopup" :id='store.state.openYoutubeId'/>

  <article :class="[itemClasses]">
    <template v-if="isPhoneOrTablet">
      <header v-if="title || description" :class="headerClasses">
        <div v-if="title" class="relative">
          <h2 :class="titleClasses" v-html="title" />
        </div>
        <p v-if="description" :class="descriptionClasses" v-html="description" />
        <div class="badje justify-center flex" v-if="avatars">
          <img :src="avatars" alt="avatar" />
          <div class="info">
            <div v-if="avatarText" class="name">{{ avatarText }}</div>
            <div v-if="avatarJob" class="text">{{ avatarJob }}</div>
          </div>
        </div>
      </header>
    </template>
    <div class="flex relative z-1">
      <div
        v-if="isDesktop"
        :class="[
          'sm:mx-auto sm:pl-2 sm:max-w-2xl sm:text-center lg:text-left lg:flex lg:items-center w-full',
          {
            'xs:order-2 lg:order-none': !reverse,
            'xs:order-2 lg:order-2': reverse,
          },
        ]"
      >
        <div>
          <template v-if="isDesktop">
            <header v-if="title || description" :class="[headerClasses, 'mb-10']">
              <div v-if="title" class="relative">
                <h2 :class="[titleClasses, 'mb-5']" v-html="title" />
              </div>
              <div v-if="description" :class="[descriptionClasses]" v-html="description" />
            </header>
            <div class="badje flex">
              <img :src="avatars" alt="avatar"  />
              <div class="info">
                <div v-if="avatarText" class="name">{{ avatarText }}</div>
                <div v-if="avatarJob" class="text">{{ avatarJob }}</div>
              </div>
            </div>
          </template>
          <ul
            v-if="items.length"
            class="xs:text-base font-medium text-black space-y-4 mt-6 sm:text-center lg:text-left"
          >
            <li class="flex space-x-2" v-for="{ id, text } in items" :key="id">
              <Icon name="check-2" class="w-4.5 xs:h-[1.5rem] text-yellow-700" aria-hidden="true" />
              <span v-text="text" />
            </li>
          </ul>

          <!--div class="mt-10">
            <DecoratedLink type="base" href="/" text="Learn more how we can help your business" icon-name="arrow-up-right" icon="right-icon" weight="semibold" />
          </div-->
        </div>
      </div>
      <div
        :class="[
          'doc-img',
          'lg:relative xs:mt-10 lg:mt-0 xs:m-auto',
          {
            'xs:-order-1 lg:order-none': !reverse,
            'xs:-order-1 lg:order-1': reverse,
          },
        ]"
      >
        <div
          :class="[
            'sm:mx-auto sm:max-w-2xl lg:px-0',
            {
              'lg:min-h-[462px]': isPrimary,
              'lg:min-h-[324px]': isSecondary,
            },
          ]"
        >
          <img
            v-if="imageName"
            class="xs:w-full xs:h-auto lg:absolute lg:inset-0 lg:m-auto lg:w-full feature-item-image rounded-lg"
            :src="imageSrc"
            alt=""
            @click="store.dispatch('openYoutubePopup', id)"
          />
          <img
            class="cursor-pointer absolute top-1/2 left-1/2 -translate-y-2/4 -translate-x-2/4"
            src="/images/Button.svg"
            alt=""
           @click="store.dispatch('openYoutubePopup', id)"
          />
        </div>
      </div>
    </div>
  </article>

</template>



<style lang="scss" scoped>
.doc-img {
  width: 366px;
  height: 420px;
}
</style>
